import { Capacitor } from "@capacitor/core";
import { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import styled from "styled-components";
import { usePwaViewportWidth } from "../hooks/useScreenWidth";
import { Row } from "../styles/Utils";

import { Camera, CameraDirection, CameraResultType, CameraSource } from "@capacitor/camera";
import { safeLog } from "../error-logger";

export enum CameraType {
  FRONT,
  BACK,
}

export const CameraModal = ({
  preferredFacingMode,
  onClose,
}: {
  preferredFacingMode: CameraType;
  onClose: (image?: string) => void;
}) => {
  const screenWidth = usePwaViewportWidth();
  const webcamRef = useRef<Webcam>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  // Detect if the device is a mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Detect if running in Capacitor app
  const isCapacitor = Capacitor.isNativePlatform();

  useEffect(() => {
    if (imageSrc == null) {
      (async () => {
        if (isCapacitor) {
          try {
            const photo = await Camera.getPhoto({
              quality: 90,
              resultType: CameraResultType.DataUrl,
              source: CameraSource.Camera,
              direction: preferredFacingMode === CameraType.BACK ? CameraDirection.Rear : CameraDirection.Front,
            });
            setImageSrc(photo.dataUrl!);
          } catch (err) {
            console.error("Camera error:", err);
            if (err instanceof Error) {
              safeLog({ type: "error", message: err.message });
            }
            onClose();
          }
        }
      })();
    }
  }, [imageSrc]);

  const capture = async () => {
    // if (isCapacitor) {
    //   try {
    //     const photo = await Camera.getPhoto({
    //       quality: 90,
    //       resultType: CameraResultType.DataUrl,
    //       source: CameraSource.Camera,
    //       direction: preferredFacingMode === CameraType.BACK ? CameraDirection.Rear : CameraDirection.Front,
    //     });
    //     setImageSrc(photo.dataUrl!);
    //   } catch (error) {
    //     console.error("Camera error:", error);
    //     onClose();
    //   }
    // } else
    if (webcamRef.current) {
      const image = webcamRef.current.getScreenshot();
      if (image) {
        setImageSrc(image);
      }
    }
  };

  const handleClose = (imageSrc?: string) => {
    if (!imageSrc) {
      // mixpanel.track("Close Camera");
    }
    onClose(imageSrc);
  };

  const handleRetake = () => {
    // mixpanel.track("Retake Photo");
    setImageSrc(null);
  };

  const videoFacingMode = isMobile && preferredFacingMode === CameraType.BACK ? "environment" : "user";

  return (
    <Overlay>
      <Modal $width={screenWidth}>
        {!imageSrc ? (
          <>
            {!isCapacitor ? (
              <>
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={1}
                  videoConstraints={{ facingMode: videoFacingMode }}
                  style={{ width: "100%", borderRadius: "10px" }}
                />
                <Row $justifyContent="center" $gap={1}>
                  <Button onClick={capture}>📸 Capture</Button>
                  <Button onClick={() => handleClose()}>❌ Cancel</Button>
                </Row>
              </>
            ) : (
              <Placeholder>📷 Camera is opening</Placeholder>
            )}
          </>
        ) : (
          <>
            <img src={imageSrc} alt="Captured" style={{ width: "100%", borderRadius: "10px" }} />
            <Row $justifyContent="center" $gap={1}>
              <Button onClick={() => handleClose(imageSrc)}>✅ Use Photo</Button>
              <Button onClick={handleRetake}>🔄 Retake</Button>
              <Button onClick={() => handleClose()}>❌ Cancel</Button>
            </Row>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div<{
  $width: number;
}>`
  background: ${(props) => props.theme.color.primaryExtraLight};
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  width: ${(props) => props.$width * 0.9 + "px"};
`;

const Button = styled.button`
  background: ${(props) => props.theme.color.primary};
  color: ${(props) => props.theme.color.tertiaryLight};
  padding: 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
`;

const Placeholder = styled.div`
  color: #fff;
  margin: 20px;
  font-size: 1rem;
`;
